/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import MasqService from 'cio/services/masq';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import DS from 'ember-data';
import { task } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import MomentService from 'cio/services/moment';
import { DATACENTER_FLAGS } from 'cio/helpers/data-center-name';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { AccountState } from 'cio/models/account';
import { IdentifierKey } from 'cio/utils/identifiers';
import AuthService from 'cio/services/auth';
import moment from 'moment-timezone';

export enum AccessSource {
  TS = 'Support team',
  CS = 'Success team',
}

export default class MasqueradeBanner extends Component {
  @service masq!: MasqService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service moment!: MomentService;
  @service auth!: AuthService;

  @tracked isAccountFormVisible = false;

  @computed(
    'account.current.{id,accountState,dataCenter,currentUser.defaultWorkspace,accessGrantedUntil}',
    'environment.current.{id,enabledIdentifiers.[]}',
    'hasMailServers.{isRunning,last.value}',
    'accessConsentTooltipText',
  )
  get displayData() {
    return [
      {
        label: 'account',
        value: this.account.current.id,
        icon: null,
        route: 'settings',
        routeModel: [],
      },
      {
        label: 'state',
        value: this.account.current.accountState,
        icon: null,
        route: 'settings.billing.your-plan',
        routeModel: [],
      },
      {
        label: 'data center',
        value: `${DATACENTER_FLAGS[this.account.current.dataCenter]} ${this.account.current.dataCenter.toUpperCase()}`,
        icon: null,
        route: 'settings.privacy',
        routeModel: [],
      },
      {
        label: 'shard',
        value: this.environment.current?.shard,
        icon: 'workspaces',
        route: 'workspace-settings',
        routeModel: [this.environment.current?.id],
      },
      {
        label: 'smtp',
        value: this.hasMailServers.isRunning ? 'loading' : this.hasMailServers.last?.value ?? false,
        icon: 'workspaces',
        route: 'actions.email.smtp',
        routeModel: [this.environment.current?.id ?? this.account.current.currentUser.defaultEnvironment],
      },
      {
        label: 'identifiers',
        value: this.environment.current.isEmailIdentified
          ? IdentifierKey.Email
          : this.environment.current.enabledIdentifiers.join(', '),
        icon: 'workspaces',
        route: 'workspace-settings.edit',
        routeModel: [this.environment.current?.id ?? this.account.current.currentUser.defaultEnvironment],
      },
    ];
  }

  get accessData() {
    let accessData = [
      {
        label: 'Support Access',
        value: this.accessValue,
        icon: 'lock-insecure',
        route: 'settings.privacy',
        routeModel: [],
        params: {
          sourceString: AccessSource.TS,
          grantedUntil: this.account.current.accessGrantedUntil,
          grantedOn: this.account.current.accessGrantedOn,
          grantedBy: this.account.current.accessGrantedBy,
          teamAccessGranted: this.account.current.supportTeamAccessEnabled,
        },
      },
    ];
    if (this.account.current.hasCsm) {
      accessData.push({
        label: 'Success Access',
        value: this.csmAccessValue,
        icon: 'lock-insecure',
        route: 'settings',
        routeModel: [],
        params: {
          sourceString: AccessSource.CS,
          grantedUntil: this.account.current.csAccessGrantedUntil,
          grantedOn: this.account.current.csAccessGrantedOn,
          grantedBy: this.account.current.csAccessGrantedBy,
          teamAccessGranted: this.account.current.successTeamAccessEnabled,
        },
      });
    }
    return accessData;
  }

  @computed('auth.user.masqPermissions')
  get accessValue() {
    let { user } = this.auth;
    if (user?.hasReadMasqAccess && this.account.current.accessGrantedUntil > moment().unix()) {
      return this.moment.formatTimestamp([this.account.current.accessGrantedUntil], {
        finalFormatter: (timestampAsMoment: moment.Moment) => timestampAsMoment.format('MMM D, YYYY'),
      });
    }
    if (user?.isSuperUser) {
      return 'Full access';
    }
    return 'No access';
  }

  @computed('auth.user.masqPermissions', 'account.current.csAccessGrantedUntil')
  get csmAccessValue() {
    let { user } = this.auth;
    if (this.account.current.csAccessGrantedUntil === -1 && user?.hasReadMasqAccess) {
      return 'Indefinite';
    }
    if (this.account.current.successTeamAccessEnabled && user?.hasReadMasqAccess) {
      return this.moment.formatTimestamp([this.account.current.csAccessGrantedUntil], {
        finalFormatter: (timestampAsMoment: moment.Moment) => timestampAsMoment.format('MMM D, YYYY'),
      });
    }
    return 'No access';
  }

  willDestroy() {
    super.willDestroy();

    document.documentElement.style.setProperty('--masq-bar-height', '0px');
  }

  hasMailServers = task(async () => {
    // The mail-servers route will return a 401 for canceled and suspended accounts, so we should
    // avoid fetching that data otherwise the user will be logged out.
    if (
      [AccountState.Canceled, AccountState.Suspended].includes(this.account.current.accountState) ||
      !this.environment.current.id
    ) {
      return 'N/A';
    }

    try {
      let servers = (await this.store.findAll('mail-server')).toArray();

      return servers.length > 0;
    } catch {
      return false;
    }
  });

  @action
  unmasquerade() {
    return this.masq.unmasquerade();
  }

  @action
  toggleReadOnly() {
    return this.masq.toggleReadOnly();
  }

  @action
  toggleAccountForm() {
    this.isAccountFormVisible = !this.isAccountFormVisible;
  }

  @action
  maybeToggleForm(evt: FlyKeyboardEvent<HTMLDivElement>) {
    if (evt.key === 'Escape' && this.isAccountFormVisible) {
      this.toggleAccountForm();
    }
  }

  @action
  setFrameBorder(element: HTMLDivElement) {
    let height = element.clientHeight;

    document.documentElement.style.setProperty('--masq-bar-height', `${height}px`);
  }
}
